import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Target, Eye, ArrowRight } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, title: "Accountability first", desc: "Every feature is built so you can prove what happened — to riders, agencies and auditors." },
  { icon: Eye, title: "Operational clarity", desc: "Real-time visibility into drivers, trips and vehicles so nothing slips through the cracks." },
  { icon: Target, title: "Built for the field", desc: "Designed with and for transportation operators who live in dispatch all day." },
];

export default function AboutPage() {
  const navigate = useNavigate();
  return (
    <div data-testid="about-page">
      <div className="bg-slate-950 pt-36 pb-20 text-white">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">About RideStamp</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            We help transportation providers account for every ride
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300">
            RideStamp was built for the operators moving people who depend on reliable transportation —
            dialysis patients, seniors, and community members. In this world, a late or undocumented
            trip isn't a minor inconvenience; it's a missed appointment and a broken trust.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid gap-16 lg:grid-cols-[1fr_1fr]">
          <div>
            <h2 className="font-display text-3xl font-bold text-slate-900">Our mission</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              To give non-emergency and non-medical transportation providers a single platform where
              drivers, trips, dispatch, fleet, compliance and incidents all come together — so every
              ride is accounted for.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              We replace scattered spreadsheets, group chats and paper logs with one operations and
              accountability platform your whole team can rely on.
            </p>
          </div>
          <div className="grid gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><v.icon className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{v.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 grid gap-6 rounded-3xl bg-slate-50 p-10 sm:grid-cols-4 lg:p-14">
          {[["Every ride", "accounted for"], ["Every driver", "accountable"], ["Every vehicle", "tracked"], ["Every trip", "documented"]].map(([a, b]) => (
            <div key={a} className="text-center">
              <div className="font-display text-xl font-bold text-blue-800">{a}</div>
              <div className="mt-1 text-sm text-slate-500">{b}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Button onClick={() => navigate("/request-demo")} data-testid="about-cta"
            className="gap-2 rounded-full bg-blue-800 px-8 py-6 text-base font-semibold text-white hover:bg-blue-900">
            Get Started <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
